"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  sumTariff,
  TARIFF_QUALIFICATIONS,
  TARIFF_YEARS,
  type TariffEntry,
  type TariffQualification,
  type TariffYear,
  tariffGrades,
} from "@/lib/tool-kit/intl-grades/ucas-tariff";

/** English strings. A translated content file overrides these through `ui`. */
export const UCAS_TARIFF_POINTS_CALCULATOR_STRINGS = {
  yearLabel: "UCAS Tariff table",
  years: {
    "2026": "2026 entry",
    "2027": "2027 entry",
  },
  yearHint:
    "UCAS publishes one table per entry year. Only the IB Higher and Standard Level points differ between these two.",
  qualificationsHeading: "Your qualifications",
  qualificationLabel: "Qualification {n}",
  gradeLabel: "Grade {n}",
  qualifications: {
    "a-level": "A level",
    "as-level": "AS level",
    epq: "Extended Project Qualification (EPQ)",
    "welsh-bacc": "Welsh Baccalaureate Advanced Skills Challenge Certificate",
    "scottish-advanced-higher": "Scottish Advanced Higher",
    "scottish-higher": "Scottish Higher",
    "btec-extended-certificate": "BTEC National Extended Certificate (2016)",
    "btec-certificate": "BTEC National Certificate (2016)",
    "btec-diploma": "BTEC National Diploma (2016)",
    "btec-extended-diploma": "BTEC National Extended Diploma (2016)",
    "t-level": "T Level (full award)",
    "ib-higher": "IB Higher Level subject (grade 1–7)",
    "ib-standard": "IB Standard Level subject (grade 1–7)",
    "ib-extended-essay": "IB Extended Essay",
    "ib-tok": "IB Theory of Knowledge",
  },
  gradeNames: {
    "P-high": "Pass, core grade A*–C",
    "P-low": "Pass, core grade D or E",
  },
  gradeOption: "{grade} — {points} points",
  addRow: "Add a qualification",
  removeRow: "Remove",
  removeRowAria: "Remove qualification {n}",
  results: "Result",
  total: "Total UCAS Tariff points",
  counted: "Qualifications counted",
  offerLabel: "Offer in Tariff points (optional)",
  offerPlaceholder: "e.g. 128",
  offerMet: "{points} above the offer — it is met.",
  offerExact: "Exactly the offer.",
  offerShort: "{points} short of the offer.",
  points: "one: point|other: points",
  breakdown: "Points by qualification",
  colQualification: "Qualification",
  colGrade: "Grade",
  colPoints: "Points",
  emptyPrompt: "Add at least one qualification to see your total.",
  caveat:
    "Most offers are written as grades such as AAB, not points. A points total only matters when a university states its offer in Tariff points, and each university decides which qualifications count.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "UCAS Tariff points ({year}): {total}",
} as const;

type Strings = typeof UCAS_TARIFF_POINTS_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface Row extends TariffEntry {
  id: number;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, qualification: "a-level", grade: "A*" },
  { id: 2, qualification: "a-level", grade: "A" },
  { id: 3, qualification: "a-level", grade: "B" },
];

function gradeLabel(
  strings: Strings,
  grade: string,
  points: number,
  fmt: Format
): string {
  const names: Record<string, string> = strings.gradeNames;
  return t(strings.gradeOption, {
    grade: names[grade] ?? grade,
    points: fmt(points),
  });
}

function QualificationRow({
  row,
  index,
  year,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  year: TariffYear;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  const grades = tariffGrades(row.qualification, year);
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[2fr_1.5fr_auto]">
      <SelectField
        id={`utp-qual-${row.id}`}
        label={t(strings.qualificationLabel, { n })}
        onChange={(qualification: TariffQualification) => {
          const kept = tariffGrades(qualification, year).find(
            (entry) => entry.grade === row.grade
          );
          onChange({
            ...row,
            qualification,
            grade: kept
              ? kept.grade
              : (tariffGrades(qualification, year)[0]?.grade ?? ""),
          });
        }}
        options={TARIFF_QUALIFICATIONS.map((value) => ({
          value,
          label: strings.qualifications[value],
        }))}
        value={row.qualification}
      />
      <SelectField
        id={`utp-grade-${row.id}`}
        label={t(strings.gradeLabel, { n })}
        onChange={(grade) => onChange({ ...row, grade })}
        options={grades.map((entry) => ({
          value: entry.grade,
          label: gradeLabel(strings, entry.grade, entry.points, fmt),
        }))}
        value={row.grade}
      />
      <button
        aria-label={t(strings.removeRowAria, { n })}
        className={BUTTON_SECONDARY}
        onClick={onRemove}
        type="button"
      >
        {strings.removeRow}
      </button>
    </div>
  );
}

function OfferLine({
  total,
  offerRaw,
  strings,
  locale,
  fmt,
}: {
  total: number;
  offerRaw: string;
  strings: Strings;
  locale: string;
  fmt: Format;
}) {
  const offer = parseNumberInput(offerRaw);
  if (offer === null) {
    return null;
  }
  const diff = total - offer;
  const pointsText = (value: number) =>
    `${fmt(value)} ${tPlural(strings.points, locale, value)}`;
  if (diff === 0) {
    return <p className="text-foreground text-sm">{strings.offerExact}</p>;
  }
  return (
    <p className="text-foreground text-sm">
      {t(diff > 0 ? strings.offerMet : strings.offerShort, {
        points: pointsText(Math.abs(diff)),
      })}
    </p>
  );
}

export default function UcasTariffPointsCalculatorWidget() {
  const strings = useToolUi(UCAS_TARIFF_POINTS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [year, setYear] = useState<TariffYear>("2027");
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(4);
  const [offerRaw, setOfferRaw] = useState("");

  const sum = sumTariff(rows, year);
  const names: Record<string, string> = strings.gradeNames;

  function changeYear(next: TariffYear) {
    setYear(next);
    // A grade that only exists in one year's table falls back to that qualification's best grade.
    setRows((current) =>
      current.map((row) =>
        tariffGrades(row.qualification, next).some(
          (entry) => entry.grade === row.grade
        )
          ? row
          : {
              ...row,
              grade: tariffGrades(row.qualification, next)[0]?.grade ?? "",
            }
      )
    );
  }

  function addRow() {
    setRows((current) => [
      ...current,
      { id: nextId, qualification: "a-level", grade: "A" },
    ]);
    setNextId((value) => value + 1);
  }

  const tableRows = sum.lines.map((line) => [
    strings.qualifications[line.qualification],
    names[line.grade] ?? line.grade,
    fmt(line.points),
  ]);

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.yearLabel}
          name="utp-year"
          onChange={changeYear}
          options={TARIFF_YEARS.map((value) => ({
            value,
            label: strings.years[value],
          }))}
          value={year}
        />
        <p className="text-muted-foreground text-xs">{strings.yearHint}</p>
        <h3 className={WIDGET_H3}>{strings.qualificationsHeading}</h3>
        <div className="space-y-4">
          {rows.map((row, index) => (
            <QualificationRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
              year={year}
            />
          ))}
        </div>
        <button className={BUTTON_SECONDARY} onClick={addRow} type="button">
          {strings.addRow}
        </button>
        <TextField
          id="utp-offer"
          label={strings.offerLabel}
          numeric
          onChange={setOfferRaw}
          placeholder={strings.offerPlaceholder}
          value={offerRaw}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {sum.lines.length === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard emphasis label={strings.total} value={fmt(sum.total)} />
              <StatCard label={strings.counted} value={fmt(sum.lines.length)} />
            </StatGrid>
            <OfferLine
              fmt={fmt}
              locale={locale}
              offerRaw={offerRaw}
              strings={strings}
              total={sum.total}
            />
            <DataTable
              caption={strings.breakdown}
              head={[
                strings.colQualification,
                strings.colGrade,
                strings.colPoints,
              ]}
              rows={tableRows}
            />
            <p className="text-muted-foreground text-sm">{strings.caveat}</p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                year,
                total: fmt(sum.total),
              })}
            />
          </>
        )}
      </section>
    </div>
  );
}
